// ── Types ──────────────────────────────────────────────────────────
//
// Domain primitives (AlarmType, AlarmDeliveryMode, ComparisonOperator, AlarmScope,
// OwnershipType, AlarmRecipient) live in src/types/dryp/Alarm.ts and are
// reachable through the global Dryp namespace.
//
// Alarm rows in the list use the wire response shape directly — no client-side
// remapping. Recipients and structure ids are not in the list payload; load them
// via getById when the wizard or detail sheet needs them.

import { Duration } from "luxon";
import type { LensAlarmResponse } from "@/endpoints/dryp/alarm";

export type Alarm = LensAlarmResponse;

// ── Label Maps ─────────────────────────────────────────────────────

export const DELIVERY_MODE_LABELS: Record<Dryp.AlarmDeliveryMode, string> = {
	immediate: "Immediately",
	daily_digest: "Daily Digest",
	weekly_digest: "Weekly Digest",
};

export const SEVERITY_LABELS: Record<Dryp.AlarmSeverity, string> = {
	ok: "OK",
	warning: "Warning",
	critical: "Critical",
};

export const DELIVERY_MODE_DESCRIPTIONS: Record<Dryp.AlarmDeliveryMode, string> = {
	immediate: "Send a notification as soon as the alarm triggers",
	daily_digest: "Last 24 hours bundled into a morning summary email at 07:00 UTC",
	weekly_digest: "Last 7 days bundled into a Monday morning summary at 07:00 UTC",
};

export const OPERATOR_LABELS: Record<Dryp.ComparisonOperator, string> = {
	">": "Greater than",
	"<": "Less than",
	"=": "Equal to",
	">=": "Greater than or equal",
	"<=": "Less than or equal",
};

export const isOrderedOperator = (operator: Dryp.ComparisonOperator): boolean =>
	operator === ">" || operator === ">=" || operator === "<" || operator === "<=";

export const isAscendingOperator = (operator: Dryp.ComparisonOperator): boolean =>
	operator === ">" || operator === ">=";

// Compact symbol form used inside condition strings (e.g. "Water flow ≤ 4 m³/h").
// Distinct from OPERATOR_LABELS, which is the title-case dropdown form shown
// standalone in the wizard. Uses proper Unicode glyphs for ≥/≤ instead of the
// ASCII compounds returned on the wire.
export const OPERATOR_PHRASE: Record<Dryp.ComparisonOperator, string> = {
	">": ">",
	"<": "<",
	"=": "=",
	">=": "≥",
	"<=": "≤",
};

// Backend constant: digests fire at this UTC hour year-round
// (mirrors DigestPeriodCalculator.SendHourUtc in output-api).
export const DIGEST_SEND_HOUR_UTC = 7;

// Returns the digest send time in the viewer's local timezone, e.g. "08:00" or
// "09:00" in Denmark depending on DST. Computed at call time so it follows the
// current DST offset.
export const digestSendTimeLocal = (locale?: string): string => {
	const anchor = new Date();
	anchor.setUTCHours(DIGEST_SEND_HOUR_UTC, 0, 0, 0);
	return anchor.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
};

export const COOLDOWN_OPTIONS = [
	{ value: 0, label: "No cooldown (notify every time)" },
	{ value: 15, label: "15 minutes" },
	{ value: 30, label: "30 minutes" },
	{ value: 60, label: "1 hour" },
	{ value: 120, label: "2 hours" },
	{ value: 360, label: "6 hours" },
	{ value: 720, label: "12 hours" },
	{ value: 1440, label: "24 hours" },
];

// ── Formatters ─────────────────────────────────────────────────────

export const formatAlarmDuration = (minutes: number | null | undefined): string | null => {
	if (minutes == null || minutes <= 0) return null;
	const units = ["days", "hours", "minutes"] as const;
	const base = Duration.fromObject({ minutes }).shiftTo(...units);
	const filtered = base.shiftTo(...units.filter(unit => base.get(unit) > 0));
	return filtered.toHuman({ maximumFractionDigits: 0, listStyle: "narrow" });
};

interface AlarmConditionFields {
	alarmType: Dryp.AlarmType;
	operator: Dryp.ComparisonOperator;
	criticalThreshold: number;
	warningThreshold?: number | null;
	durationMinutes: number | null;
	datapointCount?: number | null;
	parameter: { name: string; unit: { symbol: string } };
}

const formatTriggerQualifier = (alarm: AlarmConditionFields): string | null => {
	const duration = formatAlarmDuration(alarm.durationMinutes);
	if (duration) return `for ${duration}`;
	if (alarm.datapointCount && alarm.datapointCount > 0) {
		return `for ${alarm.datapointCount} data point${alarm.datapointCount === 1 ? "" : "s"}`;
	}
	return null;
};

export const formatAlarmCondition = (alarm: AlarmConditionFields): string => {
	const { name, unit } = alarm.parameter;
	const operator = OPERATOR_PHRASE[alarm.operator];
	const tier = (value: number, label: string) => `${operator} ${value} (${label})`;
	const thresholdPart =
		alarm.warningThreshold != null
			? `${tier(alarm.warningThreshold, "warning")} / ${tier(alarm.criticalThreshold, "critical")} ${unit.symbol}`
			: `${operator} ${alarm.criticalThreshold} ${unit.symbol}`;
	const base = `${name} ${thresholdPart}`.trim();
	const qualifier = formatTriggerQualifier(alarm);
	return qualifier ? `${base} ${qualifier}` : base;
};
